import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, timer, Subscription } from 'rxjs';
import { switchMap, takeUntil, retry, catchError, filter } from 'rxjs/operators';
import { of } from 'rxjs';

export interface PollingConfig {
  interval: number;            // ms
  retryAttempts?: number;      // default 3
  stopCondition?: () => boolean;
}

@Injectable({ providedIn: 'root' })
export class PollingService implements OnDestroy {
  private activePolls = new Map<string, Subscription>();
  private destroy$ = new Subject<void>();

  /**
   * Administra el polling internamente y guarda la Subscription por `key`.
   * Llama a `next` por cada resultado exitoso. Devuelve la Subscription por si la necesitamos.
   */
  startPolling<T>(
    key: string,
    source$: Observable<T>,
    config: PollingConfig,
    next?: (value: T) => void
  ): Subscription {
    // corta uno previo con la misma clave
    this.stopPolling(key);

    const poll$ = timer(0, config.interval).pipe(
      takeUntil(this.destroy$),
      switchMap(() =>
        source$.pipe(
          retry(config.retryAttempts ?? 3),
          catchError(err => {
            console.error(`Polling error for ${key}:`, err);
            return of(null as T | null); // no rompas el stream
          })
        )
      ),
      filter((r): r is T => r !== null)
    );

    const stream$ = config.stopCondition
      ? poll$.pipe(
          // evalúa cada 1s si hay que cortar
          takeUntil(timer(0, 1000).pipe(filter(() => config.stopCondition!())))
        )
      : poll$;

    const sub = stream$.subscribe({
      next: v => next?.(v),
      error: () => {},
      complete: () => this.activePolls.delete(key),
    });

    this.activePolls.set(key, sub);
    return sub;
  }

  stopPolling(key: string): void {
    this.activePolls.get(key)?.unsubscribe();
    this.activePolls.delete(key);
  }

  stopAllPolling(): void {
    this.activePolls.forEach(s => s.unsubscribe());
    this.activePolls.clear();
  }

  ngOnDestroy(): void {
    this.stopAllPolling();
    this.destroy$.next();
    this.destroy$.complete();
  }
}